/*
 * Block
 * @module components
 * @flow
 */

import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp } from 'styled-tools';
import { transparentize } from 'polished';
import * as R from 'ramda';

export type BlockProps = {
  children?: React$Node,
  palette?: string,
  elevation?: 0 | 1 | 2,
  rounded?: boolean,
};

const getElevation = R.pipe(
  R.propOr(0, 'elevation'),
  R.gte(0),
  R.not,
);
const boxShadowBlur = ({ elevation }) => `${12 * 2 ** (elevation - 1)}px`;
const boxShadowColor = R.pipe(
  palette('shadow', 1),
  transparentize(0.42),
);

const styles = css`
  display: inline-flex;
  box-sizing: border-box;
  ${ifProp(
    { rounded: true },
    css`
      border-radius: 6px;
    `,
  )};
  background-color: ${palette(0, true)};
  color: ${palette({ grayscale: 0 }, 1)};
  ${ifProp(
    getElevation,
    css`
      box-shadow: 0 2px ${boxShadowBlur} 1px ${boxShadowColor};
    `,
  )};
`;

/**
 * Block React-component
 * @memberof components
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @param {string} [props.palette = 'grayscale'] - current button palette
 * @param {0 | 1 | 2} [props.elevation = 1] - elevation level
 * @param {string} [props.rounded = true] - current button palette
 * @example <caption>Simple usage</caption>
 * <Block>Block</Block>
 * @returns {React.Node<*>} React-component
 */
export const Block = styled.div.attrs(() => ({
  testid: 'block',
}))`
  ${styles};
`;

Block.defaultProps = {
  palette: 'grayscale',
  elevation: 1,
  rounded: true,
  testid: 'block',
};
